/* Bridge to the local engine of Ki Para Desktop.
 *
 * When the app is served by the engine (desktop app, or a browser opened on
 * http://127.0.0.1:8010), /api/sync/local/status/ answers and every write is
 * already queued by the engine — the browser-side sale queue is not needed.
 * When the app runs on the web (kipara.store), that endpoint does not exist
 * and everything here reports "no engine".
 *
 * One poller (5 s) feeds every subscriber (footer pill, page « Ce poste »).
 */
import { useEffect, useState } from 'react';
import { apiClient } from '../services/authService';

const POLL_MS = 5000;
const listeners = new Set();
let current = { available: null, status: null }; // available: null = unknown yet
let timer = null;
let inflight = false;

export function hasShell() {
  return typeof window !== 'undefined' && !!window.kipara;
}

export function engineSnapshot() {
  return current;
}

async function poll() {
  if (inflight) return;
  inflight = true;
  try {
    const res = await apiClient.get('sync/local/status/', { timeout: 4000 });
    current = { available: true, status: res.data };
  } catch (err) {
    const code = err?.response?.status;
    // 404 = web app (no engine). Network error while an engine was seen = engine down.
    if (code === 404 || (current.available === null && code)) current = { available: false, status: null };
    else if (current.available) current = { available: true, status: current.status, unreachable: true };
    else current = { available: false, status: null };
  } finally {
    inflight = false;
  }
  listeners.forEach((fn) => { try { fn(current); } catch { /* ignore */ } });
  if (current.available === false && listeners.size) {
    // Not a desktop install: stop polling, nothing will change during this session.
    clearInterval(timer);
    timer = null;
  }
}

export function subscribeEngine(fn) {
  listeners.add(fn);
  fn(current);
  if (!timer && current.available !== false) {
    poll();
    timer = setInterval(poll, POLL_MS);
  }
  return () => listeners.delete(fn);
}

export function refreshEngine() {
  return poll();
}

/** React hook: { available, status } — available is null until the first answer. */
export function useEngine() {
  const [snap, setSnap] = useState(current);
  useEffect(() => subscribeEngine(setSnap), []);
  return snap;
}

// ---- actions on the engine ----
export const engineApi = {
  outbox: (status, limit = 300) =>
    apiClient.get('sync/local/outbox/', { params: { status: status || undefined, limit } }).then((r) => r.data.entries),
  syncNow: () => apiClient.post('sync/local/now/', {}).then(poll),
  retry: (id) => apiClient.post(`sync/local/outbox/${id}/retry/`, {}).then(poll),
  abandon: (id) => apiClient.post(`sync/local/outbox/${id}/abandon/`, {}).then(poll),
  simulateOffline: (on) => apiClient.post('sync/local/simulate-offline/', { on }).then(poll),
  resetReplica: () => apiClient.post('sync/local/reset/', {}).then(poll),
  exportUrl: () => `${apiClient.defaults.baseURL}sync/local/export/`,
};

/** Print through the desktop shell when present (silent, chosen printer),
 *  otherwise the browser dialog. target: 'ticket' | 'a4'. */
export function printDocument(target = 'a4') {
  if (hasShell() && typeof window.kipara.print === 'function') {
    return window.kipara.print({ target });
  }
  window.print();
  return Promise.resolve({ ok: true, fallback: true });
}
